import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicFaqService, FaqPayload } from '../services/publicMarketService';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store';

const REDACTED_BLUR: React.CSSProperties = {
  filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none',
};

const FaqPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state: RootState) => state.auth);
  const [data, setData] = useState<FaqPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await publicFaqService.getFaq();
        if (cancelled) return;
        if (!res.published) {
          navigate('/', { replace: true });
          return;
        }
        setData(res);
      } catch {
        if (!cancelled) setError('FAQ temporarily unavailable. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <svg className="animate-spin" style={{ width: 36, height: 36, color: '#94a3b8' }} fill="none" viewBox="0 0 24 24">
          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: 12 }}>
        <span style={{ fontSize: 32 }}>⚠️</span>
        <p style={{ color: '#64748b', fontSize: 15 }}>{error || 'FAQ not available.'}</p>
        <button onClick={() => navigate('/')} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Go Home
        </button>
      </div>
    );
  }

  const items = data.items || [];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Nunito, sans-serif' }}>
      {/* ── nav bar ── */}
      <nav style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100, padding: '0 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {isAuthenticated ? (
            <div />
          ) : (
            <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <svg width="28" height="28" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="zFaqGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#086afb" />
                    <stop offset="100%" stopColor="#e7038e" />
                  </linearGradient>
                </defs>
                <polygon points="8,8 72,8 72,23 28,57 72,57 72,72 8,72 8,57 52,23 8,23" fill="url(#zFaqGrad)" />
              </svg>
              <span style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Zogzila</span>
            </button>
          )}
          <button onClick={() => navigate(-1)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
            ← Back
          </button>
        </div>
      </nav>

      {/* ── content ── */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '28px 16px 56px' : '48px 24px 80px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 40 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>❓</div>
          <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 900, color: '#1e293b', margin: '0 0 10px' }}>
            {data.pageTitle}
          </h1>
          {data.pageSubtitle && (
            <p style={{ fontSize: isMobile ? 14 : 16, color: '#64748b', maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
              {data.pageSubtitle}
            </p>
          )}
        </div>

        {/* Items */}
        {items.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, padding: '40px 0' }}>
            No FAQ items available yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((item) => {
              const isOpen = openId === item.id;
              return (
                <div
                  key={item.id}
                  style={{
                    background: '#fff', border: `1.5px solid ${isOpen ? '#bfdbfe' : '#e2e8f0'}`,
                    borderRadius: 12, overflow: 'hidden',
                    boxShadow: isOpen ? '0 2px 12px rgba(37,99,235,0.07)' : '0 1px 4px rgba(0,0,0,0.04)',
                    transition: 'border-color 0.15s',
                  }}
                >
                  <button
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: isMobile ? '14px 16px' : '16px 22px', background: 'none', border: 'none',
                      cursor: 'pointer', textAlign: 'left', gap: 12,
                    }}
                  >
                    <span style={{ ...(!data.maskAll ? {} : REDACTED_BLUR), fontWeight: 700, fontSize: isMobile ? 14 : 15, color: '#1e293b', flex: 1, lineHeight: 1.4 }}>
                      {item.question}
                    </span>
                    <span style={{ color: '#2563eb', fontSize: 18, flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>
                      ▾
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: isMobile ? '0 16px 16px' : '0 22px 18px', borderTop: '1px solid #f1f5f9' }}>
                      <p style={{ ...(!data.maskAll ? {} : REDACTED_BLUR), margin: '12px 0 0', fontSize: isMobile ? 13 : 14, color: '#475569', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Back to home */}
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button onClick={() => navigate('/')} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
